/**
 * The one place the module reads the app's environment selection.
 *
 * Both route entries (`index.tsx`, `views/CardEditorView.tsx`) call this to
 * build the HTTP repository for the selected environment. `null` means no
 * environment is selected yet — the caller renders the notice instead of a
 * module, because there is no channel to address a request to. Falling back to
 * the browser-storage adapter here would silently fork the user's data into a
 * store no other machine can see, which is worse than asking them to pick.
 *
 * The memo is keyed on the scope values, so switching environments in the
 * search bar hands the providers a NEW repository identity — their load
 * effects already re-run on that, which is what makes the listing follow the
 * picker without any extra wiring.
 */

import { useContext, useMemo } from 'react';

import { AppContext } from 'Context/AppContext';

import { createHttpProductCardsRepository } from '../services';
import { ProductCardsRepository } from '../types';

export const useChannelRepository = (): ProductCardsRepository | null => {
  const appContext = useContext(AppContext);
  const channelKey = appContext?.channelKey ?? '';
  // TParty/TIndustry are nullable and their ids belong to the graph — the
  // server verifies both against the channel on create, so wrong values are
  // refused loudly there rather than guessed at here.
  const partyId = appContext?.get_selected_party?.()?.id ?? '';
  const industryId = appContext?.get_selected_industry?.()?.id ?? '';

  return useMemo(
    () =>
      channelKey === ''
        ? null
        : createHttpProductCardsRepository({
            csKey: channelKey,
            partyId: String(partyId),
            industryId: String(industryId),
          }),
    [channelKey, partyId, industryId]
  );
};
